import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Bell } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Alerts() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ target_type: "merchant", target_value: "", alert_type: "price_drop" });

  const load = async () => {
    const { data } = await api.get("/lifestyle/alerts");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.target_value) return;
    try {
      await api.post("/lifestyle/alerts", form);
      toast.success("Alert created");
      setForm({ target_type: "merchant", target_value: "", alert_type: "price_drop" });
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Couldn't create");
    }
  };

  const remove = async (id) => {
    await api.delete(`/lifestyle/alerts/${id}`);
    load();
  };

  return (
    <div data-testid="page-alerts">
      <PageHeader
        eyebrow="Deal Protection Alerts"
        title="Get notified when prices drop"
        subtitle="Track products, categories, or merchants. We'll ping you on cashback boosts and price drops."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full" data-testid="add-alert-btn"><Plus className="w-4 h-4 mr-2" /> New alert</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create alert</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="mb-1.5 block">Track type</Label>
                  <Select value={form.target_type} onValueChange={(v) => setForm({ ...form, target_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="merchant">Merchant</SelectItem>
                      <SelectItem value="category">Category</SelectItem>
                      <SelectItem value="product">Product</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="mb-1.5 block">Target name</Label><Input value={form.target_value} onChange={(e) => setForm({ ...form, target_value: e.target.value })} placeholder="Walmart, groceries, paper towels…" data-testid="alert-target" /></div>
                <div>
                  <Label className="mb-1.5 block">Alert when</Label>
                  <Select value={form.alert_type} onValueChange={(v) => setForm({ ...form, alert_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="price_drop">Price drops</SelectItem>
                      <SelectItem value="cashback_increase">Cashback increases</SelectItem>
                      <SelectItem value="coupon">Coupon available</SelectItem>
                      <SelectItem value="expiring">Offer expiring</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter><Button onClick={create} className="bg-emerald-700 hover:bg-emerald-800 text-white">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <Card className="border-gray-200 shadow-none rounded-2xl">
        <CardContent className="p-0">
          <div className="divide-y divide-gray-100">
            {items.map((a) => (
              <div key={a.id} className="px-5 py-4 flex items-center justify-between" data-testid={`alert-${a.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-amber-50 text-amber-700 border border-amber-100 flex items-center justify-center"><Bell className="w-4 h-4" /></div>
                  <div>
                    <div className="font-semibold capitalize">{a.target_type}: {a.target_value}</div>
                    <div className="text-xs text-gray-500 capitalize">Alert: {a.alert_type.replace(/_/g, " ")}</div>
                  </div>
                </div>
                <button onClick={() => remove(a.id)} className="p-1.5 text-gray-400 hover:text-red-600">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            {items.length === 0 && <div className="px-5 py-12 text-center text-gray-500 text-sm">No alerts yet. Track your favorite stores and products.</div>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
